import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import apiClient from '../../api/client';
import { BottomNav } from '../../components/app/DarkTheme';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Clock, Users, CreditCard, Check, ChevronLeft, ChevronRight, Sparkles, Calendar, X } from 'lucide-react';
import { toast } from 'sonner';

const MemberBook = () => {
  const navigate = useNavigate();
  const { token, logout } = useAuth();
  const [weekStart, setWeekStart] = useState(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  });
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [sessions, setSessions] = useState([]);
  const [hasValidPass, setHasValidPass] = useState(false);
  const [loading, setLoading] = useState(true);
  const [communityDates, setCommunityDates] = useState(new Set());  // 'YYYY-MM-DD' (KST) — Community가 있는 날짜
  
  const [confirmDialog, setConfirmDialog] = useState({ open: false, session: null });
  const [booking, setBooking] = useState(false);
  const [detailSession, setDetailSession] = useState(null);  // 수업 상세 Bottom Sheet

  useEffect(() => {
    if (!token) {
      navigate('/app/login');
      return;
    }
    fetchClasses();
  }, [token, selectedDate]);

  // 주(week)가 바뀌면 Community 있는 날짜 조회 (경량)
  useEffect(() => {
    if (!token) return;
    const y = weekStart.getFullYear();
    const m = String(weekStart.getMonth() + 1).padStart(2, '0');
    const d = String(weekStart.getDate()).padStart(2, '0');
    apiClient
      .get('/app/community-dates', { params: { token, start_date: `${y}-${m}-${d}`, days: 7 } })
      .then((res) => setCommunityDates(new Set(res.data?.dates || [])))
      .catch(() => setCommunityDates(new Set()));
  }, [token, weekStart]);

  const fetchClasses = async () => {
    setLoading(true);
    try {
      // 한국 서비스 기준: 로컬(KST) 날짜를 그대로 사용 (toISOString은 UTC 변환으로 날짜가 밀릴 수 있음)
      const y = selectedDate.getFullYear();
      const m = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const d = String(selectedDate.getDate()).padStart(2, '0');
      const dateStr = `${y}-${m}-${d}`;
      const response = await apiClient.get('/app/classes', {
        params: { token, date_str: dateStr }
      });
      setSessions(response.data.sessions || []);
      setHasValidPass(response.data.has_valid_pass);
    } catch (error) {
      console.error('Failed to fetch classes:', error);
      if (error.response?.status === 401) {
        logout();
        navigate('/app/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const getWeekDates = () => {
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      dates.push(d);
    }
    return dates;
  };

  const goToPreviousWeek = () => {
    const newWeekStart = new Date(weekStart);
    newWeekStart.setDate(weekStart.getDate() - 7);
    setWeekStart(newWeekStart);
    setSelectedDate(newWeekStart);
  };

  const goToNextWeek = () => {
    const newWeekStart = new Date(weekStart);
    newWeekStart.setDate(weekStart.getDate() + 7);
    setWeekStart(newWeekStart);
    setSelectedDate(newWeekStart);
  };

  const getMonthLabel = () => {
    return `${selectedDate.getFullYear()}.${String(selectedDate.getMonth() + 1).padStart(2, '0')}`;
  };

  const formatTime = (isoString) => {
    if (!isoString) return '--:--';
    const date = new Date(isoString);
    return date.toLocaleTimeString('ko-KR', { timeZone: 'Asia/Seoul', hour: '2-digit', minute: '2-digit', hour12: false });
  };

  const formatDateLabel = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);
    
    if (compareDate.getTime() === today.getTime()) return '오늘';
    if (compareDate.getTime() === tomorrow.getTime()) return '내일';
    
    const dayNames = ['일', '월', '화', '수', '목', '금', '토'];
    return dayNames[date.getDay()];
  };

  const isSelectedDate = (date) => date.toDateString() === selectedDate.toDateString();
  const isToday = (date) => date.toDateString() === new Date().toDateString();

  const handleBookClick = (session) => {
    setConfirmDialog({ open: true, session });
  };

  const handleConfirmBook = async () => {
    const session = confirmDialog.session;
    if (!session) return;
    
    setBooking(true);
    try {
      const storedMember = JSON.parse(localStorage.getItem('member') || '{}');
      const memberId = storedMember._id;
      
      if (!memberId) {
        toast.error('회원 정보를 찾을 수 없습니다');
        return;
      }
      
      await apiClient.post('/app/reservations', {
        member_id: memberId,
        class_session_id: session.id,
        pass_id: session.recommended_pass_id
      }, { params: { token } });
      
      toast.success('예약이 완료되었습니다');
      setConfirmDialog({ open: false, session: null });
      fetchClasses();
    } catch (error) {
      toast.error(error.response?.data?.detail || '예약에 실패했습니다');
    } finally {
      setBooking(false);
    }
  };

  const handleCancelReservation = async (reservationId) => {
    try {
      await apiClient.delete(`/app/reservations/${reservationId}`, { params: { token } });
      toast.success('예약이 취소되었습니다');
      fetchClasses();
    } catch (error) {
      toast.error(error.response?.data?.detail || '예약 취소에 실패했습니다');
    }
  };

  const formatInstructor = (instructor) => {
    if (!instructor) return null;
    if (/^instructor_\d+$/i.test(instructor)) return null;
    if (/^[a-z0-9_-]+$/i.test(instructor) && instructor.length < 3) return null;
    return instructor;
  };

  const weekDates = getWeekDates();

  return (
    <div className="min-h-screen bg-[#0F1115] pb-24">
      {/* Header */}
      <div className="bg-[#0F1115] sticky top-0 z-10">
        <div className="px-5 pt-12 pb-4">
          <h1 className="text-xl font-bold text-white">수업 예약</h1>
        </div>
        
        {/* Week Navigation */}
        <div className="flex items-center justify-between px-4 pb-3">
          <button 
            onClick={goToPreviousWeek}
            className="p-2 hover:bg-[#1A1D23] rounded-lg transition-colors active:scale-95"
          >
            <ChevronLeft className="h-5 w-5 text-[#52525B]" />
          </button>
          <span className="text-sm text-[#A1A1AA]">{getMonthLabel()}</span>
          <button 
            onClick={goToNextWeek}
            className="p-2 hover:bg-[#1A1D23] rounded-lg transition-colors active:scale-95"
          >
            <ChevronRight className="h-5 w-5 text-[#52525B]" />
          </button>
        </div>
        
        {/* Date Selector */}
        <div className="flex overflow-x-auto px-4 pb-4 gap-2 scrollbar-hide">
          {weekDates.map((date, idx) => {
            const selected = isSelectedDate(date);
            const today = isToday(date);
            const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
            const hasCommunity = communityDates.has(dateKey);

            return (
              <button
                key={idx}
                onClick={() => setSelectedDate(date)}
                data-testid={`book-date-btn-${dateKey}`}
                className={`
                  flex-shrink-0 flex flex-col items-center px-3.5 py-2.5 rounded-xl 
                  transition-all duration-150 min-w-[52px] active:scale-95
                  ${selected 
                    ? 'bg-[#FF7A00] text-white' 
                    : today
                      ? 'bg-[#1A1D23] text-white border border-[#FF7A00]/30'
                      : 'bg-[#1A1D23] text-[#71717A] hover:text-[#A1A1AA] hover:bg-[#22252E]'
                  }
                `}
              >
                <span className={`text-[10px] leading-none ${selected ? 'text-white/80' : ''}`}>
                  {formatDateLabel(date)}
                </span>
                <span className={`text-lg font-semibold mt-0.5 leading-none`}>
                  {date.getDate()}
                </span>
                {/* Community 표시자: 있는 날에만 렌더. 카드 높이를 고정 유지하기 위해 없어도 동일 높이의 자리표시자 유지 */}
                <span
                  className="mt-1 flex items-center gap-0.5 h-[11px] leading-none"
                  aria-hidden={!hasCommunity}
                  data-testid={hasCommunity ? `book-date-comm-marker-${dateKey}` : undefined}
                >
                  {hasCommunity && (
                    <>
                      <span
                        className={`inline-block w-[5px] h-[5px] rounded-full ${
                          selected ? 'bg-white' : 'bg-[#FF7A00]'
                        }`}
                      />
                      <span
                        className={`text-[9px] font-medium tracking-wide ${
                          selected ? 'text-white/85' : 'text-[#FF7A00]/85'
                        }`}
                      >
                        COMM
                      </span>
                    </>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Classes List */}
      <div className="px-4 space-y-2">
        {loading ? (
          <div className="text-center py-16 text-[#52525B]">로딩 중...</div>
        ) : sessions.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-[#52525B]">예정된 수업이 없습니다</p>
          </div>
        ) : (
          sessions.map((session) => {
            const instructorName = formatInstructor(session.instructor);
            const isReserved = session.booking_status === 'reserved';
            const isFull = session.current_count >= session.max_capacity;
            const canBook = session.booking_status === 'available';
            const noPass = session.booking_status === 'no_pass';
            
            return (
              <div
                key={session.id}
                className="bg-[#1A1D23] rounded-xl p-4 active:bg-[#1F222A] transition-colors cursor-pointer"
                onClick={() => setDetailSession(session)}
                data-testid={`session-card-${session.id}`}
              >
                <div className="flex justify-between items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-white">{session.name}</h3>
                      {session.session_type === 'community' && (
                        <span
                          className="inline-flex items-center gap-1 bg-[#FF7A00]/15 text-[#FF7A00] text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wider"
                          data-testid={`community-badge-${session.id}`}
                        >
                          <Sparkles className="w-2.5 h-2.5" />
                          Community
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-3 mt-2 text-sm text-[#71717A]">
                      <span className="flex items-center">
                        <Clock className="h-3.5 w-3.5 mr-1 text-[#52525B]" />
                        {formatTime(session.scheduled_at)} - {formatTime(session.end_time)}
                      </span>
                      <span className={`flex items-center ${isFull ? 'text-[#EF4444]/70' : ''}`}>
                        <Users className="h-3.5 w-3.5 mr-1 text-[#52525B]" />
                        {session.current_count}/{session.max_capacity}
                      </span>
                    </div>
                    
                    {instructorName && (
                      <p className="text-xs text-[#52525B] mt-1.5">{instructorName}</p>
                    )}
                  </div>
                  
                  <div className="flex flex-col items-end gap-1.5" onClick={(e) => e.stopPropagation()}>
                    {isReserved ? (
                      <>
                        <span className="inline-flex items-center bg-[#22C55E]/10 text-[#22C55E] text-xs font-medium px-2.5 py-1 rounded-full">
                          <Check className="h-3 w-3 mr-1" />
                          예약완료
                        </span>
                        <button
                          onClick={() => handleCancelReservation(session.reservation_id)}
                          className="text-xs text-[#52525B] hover:text-[#A1A1AA] transition-colors"
                        >
                          취소
                        </button>
                      </>
                    ) : canBook ? (
                      <button
                        onClick={() => handleBookClick(session)}
                        className="bg-[#FF7A00] hover:bg-[#FF8C42] text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors active:scale-[0.98]"
                      >
                        예약
                      </button>
                    ) : noPass ? (
                      <span className="text-xs text-[#F59E0B] px-3 py-1.5 bg-[#F59E0B]/10 rounded-lg">
                        이용권 필요
                      </span>
                    ) : isFull ? (
                      <span className="text-xs text-[#52525B] px-3 py-1.5 bg-[#22252E] rounded-lg">
                        마감
                      </span>
                    ) : (
                      <span className="text-xs text-[#52525B] px-3 py-1.5 bg-[#22252E] rounded-lg">
                        예약불가
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Pass Notice */}
      {!hasValidPass && !loading && sessions.length > 0 && (
        <div className="fixed bottom-20 left-0 right-0 px-4 z-10">
          <div className="bg-[#1A1D23] border border-[#F59E0B]/30 rounded-xl p-3 flex items-center gap-3">
            <CreditCard className="h-4 w-4 text-[#F59E0B] flex-shrink-0" />
            <p className="text-sm text-[#A1A1AA]">
              <span className="text-[#F59E0B]">이용권이 없습니다.</span> 센터에 문의해주세요.
            </p>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onOpenChange={(open) => !booking && setConfirmDialog({ open, session: confirmDialog.session })}>
        <DialogContent className="max-w-sm bg-[#1A1D23] border-[#2A2E38] text-white">
          <DialogHeader>
            <DialogTitle className="text-white text-center">예약 확인</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {confirmDialog.session && (
              <div className="text-center">
                <p className="text-lg font-semibold text-white mb-2">
                  {confirmDialog.session.name}
                </p>
                <p className="text-[#A1A1AA] text-sm">
                  {formatTime(confirmDialog.session.scheduled_at)} 수업을 예약할까요?
                </p>
              </div>
            )}
          </div>
          <DialogFooter className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => setConfirmDialog({ open: false, session: null })}
              disabled={booking}
              className="flex-1 bg-transparent border-[#3A3F4A] text-[#A1A1AA] hover:bg-[#22252E] hover:text-white"
            >
              취소
            </Button>
            <Button 
              onClick={handleConfirmBook}
              disabled={booking}
              className="flex-1 bg-[#FF7A00] hover:bg-[#FF8C42] border-0"
            >
              {booking ? '예약 중...' : '예약하기'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BottomNav activeTab="book" />

      {/* ═══ SESSION DETAIL BOTTOM SHEET ═══ */}
      {detailSession && (() => {
        const ds = detailSession;
        const isReserved = ds.booking_status === 'reserved';
        const isFull = ds.current_count >= ds.max_capacity;
        const canBook = ds.booking_status === 'available';
        const noPass = ds.booking_status === 'no_pass';
        const startDate = ds.scheduled_at ? new Date(ds.scheduled_at) : null;
        const fmtFullDate = (d) => d ? d.toLocaleDateString('ko-KR', {
          timeZone: 'Asia/Seoul', year: 'numeric', month: 'long', day: 'numeric', weekday: 'short',
        }) : '-';
        const fmtTimeRange = () => {
          if (!ds.scheduled_at) return '-';
          const start = formatTime(ds.scheduled_at);
          const end = ds.end_time ? formatTime(ds.end_time) : null;
          return end ? `${start} ~ ${end}` : start;
        };
        return (
          <div
            className="fixed inset-0 z-50 flex items-end justify-center"
            onClick={() => setDetailSession(null)}
            data-testid="session-detail-overlay"
          >
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm animate-fade-in" />
            <div
              className="relative w-full max-w-md bg-[#0F1115] border-t border-white/[0.06] rounded-t-3xl px-5 pt-3 pb-6 shadow-[0_-20px_60px_rgba(0,0,0,0.6)] animate-slide-up max-h-[85vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              data-testid="session-detail-sheet"
            >
              <div className="flex justify-center mb-3">
                <div className="w-10 h-1 rounded-full bg-white/15" />
              </div>

              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1 min-w-0">
                  {ds.session_type === 'community' && (
                    <span className="inline-flex items-center gap-1 bg-[#FF7A00]/15 text-[#FF7A00] text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-[0.15em] mb-2">
                      <Sparkles className="w-2.5 h-2.5" />
                      Community
                    </span>
                  )}
                  <h2 className="text-white text-xl font-bold leading-tight">{ds.name}</h2>
                </div>
                <button
                  type="button"
                  onClick={() => setDetailSession(null)}
                  className="w-8 h-8 rounded-full bg-white/[0.06] hover:bg-white/[0.12] active:scale-95 transition-all flex items-center justify-center flex-shrink-0 ml-3"
                  data-testid="session-detail-close"
                  aria-label="닫기"
                >
                  <X className="w-4 h-4 text-white/70" />
                </button>
              </div>

              {/* Meta */}
              <div className="space-y-2.5 mb-5">
                <div className="flex items-center gap-3 text-sm">
                  <Calendar className="w-4 h-4 text-[#71717A]" />
                  <span className="text-white/90 tabular-nums">{fmtFullDate(startDate)} · {fmtTimeRange()}</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Users className={`w-4 h-4 ${isFull ? 'text-[#EF4444]/80' : 'text-[#71717A]'}`} />
                  <span className={isFull ? 'text-[#EF4444]/80' : 'text-white/90'}>
                    정원 {ds.current_count}/{ds.max_capacity}명
                  </span>
                </div>
              </div>

              {/* Description */}
              {ds.description && ds.description.trim() && (
                <div className="border-t border-white/[0.06] pt-4 mb-5">
                  <h3 className="text-[10px] font-bold tracking-[0.25em] text-[#FF7A00] uppercase mb-2">
                    수업 소개
                  </h3>
                  <p
                    className="text-white/80 text-sm leading-relaxed whitespace-pre-wrap"
                    data-testid="session-detail-description"
                  >
                    {ds.description}
                  </p>
                </div>
              )}

              {/* CTA */}
              <div className="pt-2">
                {isReserved ? (
                  <div className="flex items-center gap-3">
                    <span className="flex-1 inline-flex items-center justify-center bg-[#22C55E]/10 text-[#22C55E] text-sm font-medium px-4 py-3 rounded-xl">
                      <Check className="h-4 w-4 mr-1.5" />
                      예약완료
                    </span>
                    <button
                      onClick={() => { handleCancelReservation(ds.reservation_id); setDetailSession(null); }}
                      className="text-sm text-white/70 hover:text-white bg-white/[0.06] hover:bg-white/[0.10] px-4 py-3 rounded-xl transition-colors"
                      data-testid="session-detail-cancel-btn"
                    >
                      예약 취소
                    </button>
                  </div>
                ) : canBook ? (
                  <button
                    onClick={() => { setDetailSession(null); handleBookClick(ds); }}
                    className="w-full bg-[#FF7A00] hover:bg-[#FF8C42] text-white text-base font-bold py-3.5 rounded-xl transition-colors active:scale-[0.98]"
                    data-testid="session-detail-book-btn"
                  >
                    예약하기
                  </button>
                ) : noPass ? (
                  <div className="w-full bg-[#F59E0B]/10 text-[#F59E0B] text-sm font-medium py-3 rounded-xl text-center">
                    이용권이 필요합니다
                  </div>
                ) : isFull ? (
                  <div className="w-full bg-white/[0.05] text-white/40 text-sm py-3 rounded-xl text-center">
                    정원 마감
                  </div>
                ) : (
                  <div className="w-full bg-white/[0.05] text-white/40 text-sm py-3 rounded-xl text-center">
                    예약 불가
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Slide-up & fade-in keyframes (shared with score sheet) */}
      <style>{`
        @keyframes fade-in { from{opacity:0} to{opacity:1} }
        .animate-fade-in { animation: fade-in 0.18s ease-out forwards; }
        @keyframes slide-up { from{transform:translateY(100%);opacity:0.4} to{transform:translateY(0);opacity:1} }
        .animate-slide-up { animation: slide-up 0.28s cubic-bezier(0.16,1,0.3,1) forwards; }
      `}</style>
    </div>
  );
};

export default MemberBook;
